'use memo';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import type { AiChatPageContext } from '@/features/global-ai-chat/api/types';
import { getSessionDisplayTitle } from '@/features/global-ai-chat/api/utils';
import { AiChatContextRegistryProvider } from '@/features/global-ai-chat/context/AiChatContextRegistry';
import { useActiveAiChatContext } from '@/features/global-ai-chat/hooks/useActiveAiChatContext';
import { useDefaultAiChatModel } from '@/features/global-ai-chat/hooks/useDefaultAiChatModel';
import { useReconcileActiveSession } from '@/features/global-ai-chat/hooks/useReconcileActiveSession';
import { useToggleAiChatByKeyPress } from '@/features/global-ai-chat/hooks/useToggleAiChatByKeyPress';
import {
  useAiChatMessagesQuery,
  useAiChatModelsQuery,
  useAiChatSessionsQuery,
  useCreateAiChatSessionMutation,
  useSendAiChatMessageMutation,
} from '@/features/global-ai-chat/query';
import { useGlobalAiChatStore } from '@/features/global-ai-chat/store/globalAiChatStore';
import { orpc } from '@/lib/api/orpc';
import { isOrpcError } from '@/lib/api/orpc/error-adapter';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { AiChatComposer, type AiChatComposerHandle } from './AiChatComposer';
import { AiChatContextPill } from './AiChatContextPill';
import { AiChatEmptyState } from './AiChatEmptyState';
import { AiChatFab } from './AiChatFab';
import { AiChatMessageList } from './AiChatMessageList';
import { AiChatPanel } from './AiChatPanel';
import { AiChatSessionList } from './AiChatSessionList';

/** 楽観表示するユーザー発言の擬似メッセージ ID（サーバー再取得で実メッセージに置き換わる）。 */
const OPTIMISTIC_USER_MESSAGE_ID = '__optimistic_user_message__';

export function GlobalAiChatWidget() {
  return (
    <AiChatContextRegistryProvider>
      <GlobalAiChatWidgetInner />
    </AiChatContextRegistryProvider>
  );
}

function GlobalAiChatWidgetInner() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat',
  });
  const activeContext = useActiveAiChatContext();
  const {
    isOpen,
    activeSessionId,
    selectedModelId,
    setOpen,
    toggleOpen,
    setActiveSessionId,
    setSelectedModelId,
    dismissedContextIds,
    dismissContextId,
  } = useGlobalAiChatStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      activeSessionId: state.activeSessionId,
      selectedModelId: state.selectedModelId,
      setOpen: state.setOpen,
      toggleOpen: state.toggleOpen,
      setActiveSessionId: state.setActiveSessionId,
      setSelectedModelId: state.setSelectedModelId,
      dismissedContextIds: state.dismissedContextIds,
      dismissContextId: state.dismissContextId,
    })),
  );

  const queryClient = useQueryClient();
  const sessionsQuery = useAiChatSessionsQuery();
  const modelsQuery = useAiChatModelsQuery();
  const messagesQuery = useAiChatMessagesQuery(activeSessionId);
  const createSessionMutation = useCreateAiChatSessionMutation();
  const sendMessageMutation = useSendAiChatMessageMutation();

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // 送信直後〜返信到着までの楽観的表示用。送ったユーザー発言を即座に出し、
  // 返信が返るまではアシスタント側だけローディング表示する。
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null,
  );
  // 送信時点のサーバー側ユーザー発言数。これを超えたら送信分が反映されたとみなす。
  const [pendingBaselineUserCount, setPendingBaselineUserCount] = useState(0);
  // 送信失敗時、発言を消さず残してリトライできるよう保持する。
  // chatSessionId を持たせ、表示はアクティブセッションと一致する時のみ行う
  // （自動 reconcile 等で別セッションへ切り替わった際に別画面へ残らないようにする）。
  const [failedSend, setFailedSend] = useState<{
    content: string;
    context: AiChatPageContext | null;
    chatSessionId: string | null;
  } | null>(null);

  // `?? []` を直接書くと毎回新しい配列が生成され、依存配列が常に変わって effect が無限に動く。
  const sessions = useMemo(
    () => sessionsQuery.data ?? [],
    [sessionsQuery.data],
  );
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const messages = useMemo(
    () => messagesQuery.data ?? [],
    [messagesQuery.data],
  );

  // サーバー上のメッセージ + 楽観的に表示する送信直後のユーザー発言。
  const displayMessages = useMemo(() => {
    if (pendingUserMessage === null) return messages;
    // refetch で実ユーザー発言がキャッシュへ入った後、pendingUserMessage を解除する
    // までの間に「実発言 + 楽観発言」が同時描画され二重表示になっていた（特に初回送信）。
    // 送信時点のユーザー発言数（baseline）より増えていれば、送信した発言が
    // サーバーから返ってきたとみなし楽観表示を取り下げる（解除タイミングに依存せず重複を防ぐ）。
    // コンテンツ一致ではなく件数で判定するため、同一文言の連投でも誤抑制しない。
    const userMessageCount = messages.reduce(
      (count, message) => (message.role === 'user' ? count + 1 : count),
      0,
    );
    if (userMessageCount > pendingBaselineUserCount) return messages;
    return [
      ...messages,
      {
        messageId: OPTIMISTIC_USER_MESSAGE_ID,
        role: 'user' as const,
        content: pendingUserMessage,
        timestamp: new Date().toISOString(),
      },
    ];
  }, [messages, pendingUserMessage, pendingBaselineUserCount]);

  const composerRef = useRef<AiChatComposerHandle | null>(null);

  useReconcileActiveSession(sessions, sessionsQuery.isPending);
  useDefaultAiChatModel(models);
  // Cmd+J / Ctrl+J で開閉（Notion の AI 起動と同じ）。
  useToggleAiChatByKeyPress();

  // パネルを開いた時にコンポーザーへフォーカス
  useEffect(() => {
    if (isOpen) {
      composerRef.current?.focus();
    }
  }, [isOpen]);

  const activeSession = useMemo(
    () =>
      sessions.find((session) => session.chatSessionId === activeSessionId) ??
      null,
    [activeSessionId, sessions],
  );

  // 履歴表示用: メッセージ未送信の空セッション（preview なし）は除外する。
  // 「新規」直後や送信失敗で生成されたセッションが履歴ノイズにならないようにする
  // （アクティブな空セッションはパネル本体に空状態が出るため履歴には不要）。
  // reconcile / activeSession 判定には full の `sessions` を使い続ける。
  const historySessions = useMemo(
    () => sessions.filter((session) => Boolean(session.preview?.trim())),
    [sessions],
  );

  const isActiveSessionClosed = activeSession?.status === 'closed';
  // 新規セッションの初回送信中はプレビュー未確定のため、送信内容を仮タイトルに使う。
  const displayTitle =
    pendingUserMessage && !activeSession?.preview
      ? pendingUserMessage
      : getSessionDisplayTitle(activeSession, t('title'));

  const handleStartNewChat = async () => {
    try {
      const session = await createSessionMutation.mutateAsync({
        currentPagePath: activeContext?.sourceRoute,
      });
      setActiveSessionId(session.chatSessionId);
      setIsHistoryOpen(false);
      setFailedSend(null);
    } catch (error) {
      console.error('[GlobalAiChat] createSession failed:', error);
      toast({
        description: t('createSessionErrorMessage'),
        variant: 'destructive',
      });
    }
  };

  const handleSend = async (
    content: string,
    context: AiChatPageContext | null,
  ) => {
    const currentPagePath = context?.sourceRoute;
    // SendMessageRequest.context: BE は `{ session?, currentPagePath? }` を受け取る。
    // session は今のところ FE で組まないので currentPagePath だけ送る。
    // agent（モデル選択）は選択中のものを送る。未選択時は省略し BE 既定（help）に委ねる。
    const sendInput = {
      content,
      ...(currentPagePath !== undefined && {
        context: { currentPagePath },
      }),
      ...(selectedModelId !== null && { agent: selectedModelId }),
    };
    // 送ったユーザー発言を即座に表示する（返信が返るまではアシスタント側のみローディング）。
    // 送信前のユーザー発言数を baseline として記録し、refetch で増えたら楽観表示を取り下げる。
    setPendingBaselineUserCount(
      messages.reduce(
        (count, message) => (message.role === 'user' ? count + 1 : count),
        0,
      ),
    );
    setPendingUserMessage(content);
    setFailedSend(null);
    // 失敗時に「どのセッション宛ての発言か」を記録できるよう try の外で保持する。
    let targetSessionId = activeSessionId;
    try {
      if (!targetSessionId || isActiveSessionClosed) {
        const session = await createSessionMutation.mutateAsync({
          currentPagePath,
        });
        targetSessionId = session.chatSessionId;
        setActiveSessionId(targetSessionId);
      }

      try {
        await sendMessageMutation.mutateAsync({
          chatSessionId: targetSessionId,
          input: sendInput,
        });
      } catch (error) {
        // セッション不在（404: stale な activeSessionId 等）の時だけリカバリ。
        // それ以外のエラーはそのまま上位 catch へ。
        if (!(isOrpcError(error) && error.status === 404)) {
          throw error;
        }
        console.warn(
          '[GlobalAiChat] active session not found (404), recreating and retrying:',
          error.message,
        );
        const session = await createSessionMutation.mutateAsync({
          currentPagePath,
        });
        targetSessionId = session.chatSessionId;
        setActiveSessionId(targetSessionId);
        await sendMessageMutation.mutateAsync({
          chatSessionId: targetSessionId,
          input: sendInput,
        });
      }

      // user / assistant 両メッセージを取得しきってから楽観表示を解除する
      // （途中で消えて再表示される視覚的なちらつきを防ぐ）。
      await queryClient.refetchQueries({
        queryKey: orpc.aiOperatorChat.getSession.key({
          input: { chatSessionId: targetSessionId },
        }),
      });
    } catch (error) {
      console.error('[GlobalAiChat] sendMessage failed:', error);
      // サーバー/ネットワーク起因（5xx・通信不可）と一般エラーで文言を分ける。
      const isServerSide = isOrpcError(error) && error.status >= 500;
      toast({
        description: isServerSide
          ? t('sendMessageServiceErrorMessage')
          : t('sendMessageErrorMessage'),
        variant: 'destructive',
      });
      // 発言を消さずリトライできるよう保持する（rethrow しないので入力欄は空のまま）。
      setFailedSend({ content, context, chatSessionId: targetSessionId });
    } finally {
      // 成功時: 再取得済みの実メッセージへ、失敗時: 楽観表示を取り消す（失敗バブルへ移行）。
      setPendingUserMessage(null);
    }
  };

  const handleRetrySend = () => {
    if (!failedSend) return;
    void handleSend(failedSend.content, failedSend.context);
  };

  const shouldShowContextPill = Boolean(
    activeContext && !dismissedContextIds.includes(activeContext.id),
  );

  // 失敗バブルは、その発言を送ろうとしたセッションが今アクティブな場合のみ表示する。
  const visibleFailedSend =
    failedSend && failedSend.chatSessionId === activeSessionId
      ? failedSend
      : null;

  // タイトル領域: Popover trigger 兼用のボタン。クリックで履歴ドロップダウンを開く。
  const titleNode = (
    <Popover open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-w-0 max-w-full items-center gap-1 rounded-md px-2 py-1 -mx-2',
            'hover:bg-neutral-fill-hovered focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary-border',
          )}
          aria-expanded={isHistoryOpen}
          aria-label={t('historyAction')}
        >
          <Text
            variant="label"
            className="block min-w-0 flex-1 truncate text-neutral-on-fill"
          >
            {displayTitle}
          </Text>
          <span className="shrink-0 text-neutral-muted-on-fill" aria-hidden>
            <Icon iconName="ChevronDownIcon" size={14} inheritColor />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        // PopoverContent デフォルトは z-50。AiChatPanel が z-60 なので、被らないよう上に乗せる。
        className="z-70 w-80 max-h-96 overflow-y-auto p-0"
      >
        <AiChatSessionList
          sessions={historySessions}
          activeSessionId={activeSessionId}
          hasNextPage={sessionsQuery.hasNextPage}
          isFetchingNextPage={sessionsQuery.isFetchingNextPage}
          onSelect={(sessionId) => {
            setActiveSessionId(sessionId);
            setIsHistoryOpen(false);
            setFailedSend(null);
          }}
          onFetchNextPage={() => void sessionsQuery.fetchNextPage()}
        />
      </PopoverContent>
    </Popover>
  );

  return (
    <>
      {!isOpen ? (
        <AiChatFab onClick={toggleOpen} ariaLabel={t('openButtonAriaLabel')} />
      ) : null}

      <AiChatPanel
        isOpen={isOpen}
        title={titleNode}
        onClose={() => setOpen(false)}
        headerActions={
          <ActionButton
            type="button"
            size="sm"
            variant="mutedGhost"
            icon="MessageCirclePlusIcon"
            onClick={() => void handleStartNewChat()}
          >
            {t('newChatAction')}
          </ActionButton>
        }
        footer={
          <div className="flex flex-col gap-2">
            {shouldShowContextPill && activeContext ? (
              <AiChatContextPill
                label={activeContext.label}
                iconName={activeContext.iconName}
                onDismiss={() => dismissContextId(activeContext.id)}
              />
            ) : null}
            <AiChatComposer
              ref={composerRef}
              models={models}
              selectedModelId={selectedModelId}
              placeholder={t('composerPlaceholder')}
              isSending={sendMessageMutation.isPending}
              isDisabled={isActiveSessionClosed}
              disabledMessage={t('composerDisabledClosed')}
              onSelectModel={setSelectedModelId}
              onSend={(content) =>
                handleSend(
                  content,
                  shouldShowContextPill && activeContext ? activeContext : null,
                )
              }
            />
          </div>
        }
      >
        {displayMessages.length > 0 || visibleFailedSend ? (
          <AiChatMessageList
            messages={displayMessages}
            isAwaitingReply={sendMessageMutation.isPending}
            failedMessage={visibleFailedSend}
            onRetry={handleRetrySend}
          />
        ) : (
          <AiChatEmptyState
            title={t('emptyTitle')}
            description={t('emptyDescription')}
          />
        )}

        {sessionsQuery.isPending || modelsQuery.isPending ? (
          <div className="px-4 pb-4">
            <Text variant="labelExSmall" className="text-neutral-muted-on-fill">
              {t('loading')}
            </Text>
          </div>
        ) : null}
      </AiChatPanel>
    </>
  );
}
